import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, RegisterCompanyData } from '../services/auth.service';
import { useTranslation } from 'react-i18next';
import { useAppGuide } from '../components/AppGuide';
import AuthShell from '../components/AuthShell';
import ContactInfoDialog from '../components/ContactInfoDialog';
import {
    ArrowRight,
    Briefcase,
    CheckCircle,
    Eye,
    EyeOff,
    FileText,
    Lock,
    Mail,
    Phone,
    User,
    UserPlus
} from 'react-feather';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

// Register uses the same shadcn-style auth shell as Login while preserving the company onboarding flow.

export const Register: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const appGuide = useAppGuide();
    const [formData, setFormData] = useState<RegisterCompanyData>({
        companyName: '',
        companyDomain: '',
        username: '',
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        telephone: '',
        description: ''
    });
    const [error, setError] = useState<string>('');
    const [showPassword, setShowPassword] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showContactInfoDialog, setShowContactInfoDialog] = useState(false);
    const [registeredCompanyName, setRegisteredCompanyName] = useState('');

    // 预填示例数据的方法
    const fillDemoData = useCallback(() => {
        setFormData(prevData => ({
            ...prevData,
            companyName: '示例科技有限公司',
            username: 'admin',
            //password: '123456',
            firstName: '管理员',
            lastName: '先生',
            email: 'admin@example.com',
            telephone: '1380013XXXX',
            description: '这是一个用于演示的示例公司'
        }));
    }, []);

    // 注册预填数据回调到AppGuide
    useEffect(() => {
        if (appGuide?.registerFillDemoData) {
            appGuide.registerFillDemoData(fillDemoData);
        }
    }, [appGuide, fillDemoData]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleClickShowPassword = () => setShowPassword(!showPassword);

    // Handle success dialog close and navigate to login
    const handleSuccessDialogClose = () => {
        setShowSuccessDialog(false);
        navigate('/login');
    };

    // Handle showing contact info from success dialog
    const handleShowContactInfo = () => {
        setShowContactInfoDialog(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        try {
            // Make sure only essential fields are required
            const requiredFields = ['companyName', 'username', 'password'];
            for (const field of requiredFields) {
                if (!formData[field as keyof RegisterCompanyData]) {
                    setError(`Please fill in the ${field} field`);
                    return;
                }
            }

            const dataToSend: Partial<RegisterCompanyData> = { ...formData };
            // Add current language to registration data
            const currentLanguage = i18n.language === 'zh' ? 'zh' : 'en';
            dataToSend.language = currentLanguage;
            
            // Remove empty fields to make them use defaults in the backend
            Object.keys(dataToSend).forEach(key => {
                const typedKey = key as keyof RegisterCompanyData;
                if (!dataToSend[typedKey] && key !== 'language') delete dataToSend[typedKey];
            });

            await authService.registerCompany(dataToSend as RegisterCompanyData);
            
            // Store company name for success dialog
            setRegisteredCompanyName(formData.companyName);
            
            // 如果在引导流程中，调用 completeRegistration
            if (appGuide?.completeRegistration) {
                appGuide.completeRegistration();
            }
            
            // Show success dialog instead of navigating immediately
            setShowSuccessDialog(true);
        } catch (err: any) {
            console.error('Registration error:', err);
            const errorMsg = err.response?.data?.error || 'errorGeneric';
            console.log('Error response:', err.response?.data);
            setError(t(errorMsg, { defaultValue: `Registration error: ${errorMsg}` }));
        }
    };

    return (
        <AuthShell
            action="signin"
            contentClassName="mx-auto grid w-full max-w-7xl items-start gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(460px,0.9fr)] lg:px-8 lg:py-14"
            helpLabel={t('guide.helpButton')}
            onHelp={appGuide?.startGuide}
        >
            <section className="max-w-2xl pt-4">
                <div className="login-eyebrow mb-5 inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1 text-sm font-medium text-muted-foreground shadow-sm">
                    <UserPlus className="h-4 w-4 text-accent" aria-hidden="true" />
                    {t('registerCompany')}
                </div>
                <h1 className="text-4xl font-semibold leading-tight text-foreground sm:text-5xl">
                    {t('registerTitle')}
                </h1>
                <p className="mt-5 max-w-xl text-lg leading-8 text-muted-foreground">
                    {t('landingSubheader')}
                </p>
            </section>

            <Card className="login-card border-border/80 shadow-soft">
                <CardHeader className="space-y-2">
                    <CardTitle className="text-2xl">{t('registerTitle')}</CardTitle>
                    <CardDescription>{t('optionalFields', { defaultValue: '可选信息' })}</CardDescription>
                </CardHeader>
                <CardContent>
                    {error && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                    <form onSubmit={handleSubmit} noValidate className="space-y-5 register-form">
                        <div className="space-y-2">
                            <Label htmlFor="company-name">{t('companyNameLabel')}</Label>
                            <div className="relative">
                                <Briefcase className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                                <Input
                                    required
                                    id="company-name"
                                    name="companyName"
                                    value={formData.companyName}
                                    onChange={handleChange}
                                    className="pl-9"
                                />
                            </div>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="username">{t('usernameLabel')}</Label>
                                <div className="relative">
                                    <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                                    <Input
                                        required
                                        id="username"
                                        name="username"
                                        value={formData.username}
                                        onChange={handleChange}
                                        autoComplete="username"
                                        className="pl-9"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password">{t('passwordLabel')}</Label>
                                <div className="relative">
                                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                                    <Input
                                        required
                                        id="password"
                                        name="password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={formData.password}
                                        onChange={handleChange}
                                        autoComplete="new-password"
                                        className="pl-9 pr-10"
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
                                        onClick={handleClickShowPassword}
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                                    </button>
                                </div>
                                <p className="text-xs leading-5 text-muted-foreground">{t('passwordMinChars')}</p>
                            </div>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="telephone">{t('telephoneLabel')}</Label>
                                <div className="relative">
                                    <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                                    <Input id="telephone" name="telephone" value={formData.telephone} onChange={handleChange} autoComplete="tel" className="pl-9" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email">{t('emailLabel')}</Label>
                                <div className="relative">
                                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} autoComplete="email" className="pl-9" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="firstName">{t('firstNameLabel')}</Label>
                                <Input id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} autoComplete="given-name" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="lastName">{t('lastNameLabel')}</Label>
                                <Input id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} autoComplete="family-name" />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="description">{t('descriptionLabel')}</Label>
                            <div className="relative">
                                <FileText className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                                <textarea
                                    id="description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows={3}
                                    className="flex min-h-[88px] w-full rounded-md border border-input bg-background px-3 py-2 pl-9 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                />
                            </div>
                        </div>

                        <Button type="submit" size="lg" className="login-primary-action w-full">
                            {t('registerButton')}
                            <ArrowRight className="h-4 w-4" aria-hidden="true" />
                        </Button>
                        <p className="text-center text-sm text-muted-foreground">
                            {t('haveAccount')}{' '}
                            <button
                                type="button"
                                className="font-medium text-primary underline-offset-4 hover:underline"
                                onClick={() => navigate('/login')}
                            >
                                {t('signInLink')}
                            </button>
                        </p>
                    </form>
                </CardContent>
            </Card>

            {showSuccessDialog && (
                <div className="industrial-dialog-backdrop" role="presentation" onMouseDown={handleSuccessDialogClose}>
                    <div className="industrial-dialog" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
                        <header className="industrial-dialog-header">
                            <div>
                                <h2 className="industrial-dialog-title">
                                    <CheckCircle className="industrial-icon" aria-hidden="true" />
                                    {t('registration.successTitle', 'Registration Successful')}
                                </h2>
                            </div>
                            <button type="button" className="industrial-dialog-close" onClick={handleSuccessDialogClose} aria-label="Close">
                                ×
                            </button>
                        </header>
                        <div className="industrial-dialog-content">
                            <Alert className="border-emerald-500/40 bg-emerald-500/10 text-emerald-900">
                                <AlertDescription>
                                    {t('registration.successMessage', {
                                        companyName: registeredCompanyName,
                                        defaultValue: `Your company "${registeredCompanyName}" has been registered successfully!`
                                    })}
                                </AlertDescription>
                            </Alert>
                            <p className="mt-4 text-sm leading-6 text-muted-foreground">
                                {t('registration.pendingApproval', 'Your account is pending approval by the system administrator. You will be able to log in once your account has been approved.')}
                            </p>
                            <div className="mt-4 border-t border-border pt-4">
                                <p className="flex items-start gap-2 text-sm leading-6 text-muted-foreground">
                                    <Phone className="mt-1 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                                    {t('registration.contactAdminHint', 'If you need to expedite the approval process, please contact the system administrator.')}
                                </p>
                            </div>
                        </div>
                        <footer className="industrial-dialog-actions">
                            <Button type="button" variant="outline" onClick={handleShowContactInfo}>
                                <Phone className="h-4 w-4" aria-hidden="true" />
                                {t('registration.viewContactInfo', 'Contact Admin')}
                            </Button>
                            <Button type="button" onClick={handleSuccessDialogClose}>
                                {t('registration.goToLogin', 'Go to Login')}
                            </Button>
                        </footer>
                    </div>
                </div>
            )}

            <ContactInfoDialog
                open={showContactInfoDialog}
                onClose={() => setShowContactInfoDialog(false)}
            />
        </AuthShell>
    );
}; 
